import { useRef } from "react";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";

import { formatTime } from "@/view/GeniusContent/utils";
import {
  geniusFigureCss,
  geniusVideoWrapperCss,
  playerColor,
} from "./GeniusVideo.style";
import type {
  GenerationGeniusContent,
  WistiaPlayerCustomEvent,
} from "@/view/GeniusContent/GeniusContent.interface";

interface GeniusVideoProps {
  wistiaId: string;
  content: GenerationGeniusContent;
  setDuration: (_: string) => void;
  showBlueBackground?: boolean;
  isVideoOnly?: boolean;
}

const GeniusVideo = ({
  wistiaId,
  content,
  setDuration,
  showBlueBackground = false,
  isVideoOnly = false,
}: GeniusVideoProps) => {
  const { trackAction, getEventName } = useTracker();
  const handleReady = ({ target }: WistiaPlayerCustomEvent) => {
    const { duration: videoDuration } = target;
    setDuration(formatTime(videoDuration));
  };
  const firstPlayRef = useRef(false);
  const handlePlay = () => {
    if (firstPlayRef.current) return;
    firstPlayRef.current = true;
    trackAction({
      event_type: eventTypes.click,
      event_name: getEventName(
        objectTypes.generationGeniusVideo,
        actionPrefixes.initiate,
        actionNames.start,
      ),
      dimension_properties: {
        description: "Playing generation genius video on wistia",
        content_id: content.id,
        content_type: "GenerationGeniusVideo",
      },
    });
  };

  return (
    <div css={geniusVideoWrapperCss(showBlueBackground)}>
      <figure css={geniusFigureCss(isVideoOnly)}>
        <WistiaPlayer
          data-testid="wistia-player"
          mediaId={wistiaId}
          onPlay={handlePlay}
          onCanPlay={handleReady}
          onCanPlayThrough={handleReady}
          playerColor={playerColor(showBlueBackground)}
        />
      </figure>
    </div>
  );
};

export default GeniusVideo;
